import React, { useState, useEffect } from "react";
import OutstandingBalanceFilters from "./Components/OutstandingBalanceComponents/OutstandingBalanceFilters";
import { SearchIcon, Settings, RotateCw, X, FilterX } from "lucide-react";
import AllTable, {
  defaultColumns,
} from "./Components/OutstandingBalanceComponents/AllTable";
import { IconButton, Menu, MenuItem, Checkbox } from "@mui/material";
import { errorToast, successToast } from "../../../services/toast.service";
import { dummyOutstandingBalanceData } from "../../../dummyData";

const OutstandingBalanceScreen = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(
    defaultColumns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}),
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refetchFn, setRefetchFn] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    status: "",
    sortBy: "",
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500); // Wait 500ms after last keystroke

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleColumnToggle = columnId => {
    const column = defaultColumns.find(col => col.id === columnId);
    if (column?.required) return;

    setVisibleColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  // Create the refresh handler here
  const handleRefresh = async () => {
    if (refetchFn) {
      setIsRefreshing(true);
      try {
        await refetchFn();
        successToast("Outstanding balances refreshed");
      } catch (error) {
        errorToast("Failed to refresh outstanding balances");
      } finally {
        setIsRefreshing(false);
      }
    }
  };

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleFilterChange = newFilters => {
    setFilters(newFilters);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({
      fromDate: "",
      toDate: "",
      status: "",
      sortBy: "",
    });
  };

  // Calculate total outstanding balance
  const totalOutstanding = dummyOutstandingBalanceData.reduce(
    (sum, item) => sum + item.pendingAmount,
    0,
  );

  // Format currency helper
  const formatCurrency = amount => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  return (
    <div className="bg-white h-[calc(100vh-120px)]">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <h1 className="text-2xl font-semibold font-outfit text-gray-800">
          Outstanding Balance
        </h1>

        {/* Search and Settings Section */}
        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="w-[550px]">
            <div className="relative">
              <SearchIcon
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by name, phone number, or address..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 hover:bg-gray-100 p-1 rounded-full">
                  <X size={16} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              onClick={handleRefresh}
              className="hover:bg-gray-100"
              disabled={isRefreshing}
              title="Refresh">
              <RotateCw
                size={20}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
            <IconButton
              onClick={handleSettingsClick}
              className="hover:bg-gray-100"
              title="Column Settings">
              <Settings size={20} className="text-gray-600" />
            </IconButton>
          </div>
        </div>
      </div>

      {/* Column Settings Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseSettings}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: 200,
          },
        }}>
        <div className="px-3 py-2 border-b border-gray-100">
          <p className="text-sm font-medium text-gray-600">Show Columns</p>
        </div>
        {defaultColumns.map(column => (
          <MenuItem
            key={column.id}
            onClick={() => handleColumnToggle(column.id)}
            disabled={column.required}
            dense>
            <Checkbox
              checked={visibleColumns[column.id]}
              disabled={column.required}
              size="small"
              sx={{
                color: "#5652B0",
                "&.Mui-checked": {
                  color: "#5652B0",
                },
              }}
            />
            <span
              className={`text-sm ${column.required ? "text-gray-400" : ""}`}>
              {column.label}
              {column.required && " *"}
            </span>
          </MenuItem>
        ))}
      </Menu>

      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <OutstandingBalanceFilters
            onFilterChange={handleFilterChange}
            filters={filters}
          />
          {(searchQuery ||
            Object.values(filters).some(value => value !== "")) && (
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 bg-gray-50 
                       hover:bg-gray-100 rounded-lg transition-colors">
              <FilterX size={16} />
              Clear Filters
            </button>
          )}
        </div>

        {/* Summary Card */}
        <div className="mb-6 p-4 bg-[#F9FAFB] border border-gray-200 rounded-lg">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-sm font-medium text-gray-500">
                Total Outstanding Balance
              </h3>
              <p className="text-2xl font-semibold text-[#5652B0]">
                {formatCurrency(totalOutstanding)}
              </p>
            </div>
            <div className="text-sm text-gray-500">
              <p>Total Customers: {dummyOutstandingBalanceData.length}</p>
              <p>Last Updated: {new Date().toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        <AllTable
          visibleColumns={visibleColumns}
          onColumnToggle={handleColumnToggle}
          setRefetchFn={setRefetchFn}
          searchQuery={debouncedSearch}
          filters={filters}
        />
      </div>
    </div>
  );
};

export default OutstandingBalanceScreen;
